// I documenti legali, uno per lingua, compilati dal markdown in fase di build.
//
// Il caricamento e' `eager` di proposito: e' esattamente cio' che faceva il JSX
// che questi file hanno sostituito (TermsContent.tsx e PrivacyContent.tsx erano
// importati dalla pagina E dal modale, entrambi non lazy), e soprattutto e' cio'
// che permette allo script di prerender di rendere le sedici pagine legali senza
// attendere un import dinamico dentro renderToString.

import { RULES_LANGS, type RulesLang, DEFAULT_LANG } from '../seo/routes';

export type LegalDocKind = 'terms' | 'privacy';

export interface LegalDoc {
  title: string;
  /** Data civile ISO (YYYY-MM-DD) dal frontmatter. */
  updated: string;
  html: string;
}

const MODULES = import.meta.glob<{ default: { meta: { title?: string; updated?: string }; html: string } }>(
  './legal/*/*.md',
  { eager: true },
);

function buildIndex(): Record<RulesLang, Record<LegalDocKind, LegalDoc>> {
  const index = {} as Record<RulesLang, Record<LegalDocKind, LegalDoc>>;
  for (const [path, mod] of Object.entries(MODULES)) {
    const match = /\.\/legal\/([^/]+)\/(terms|privacy)\.md$/.exec(path);
    if (!match) continue;
    const [, lang, kind] = match as unknown as [string, RulesLang, LegalDocKind];
    const doc = mod.default;
    const entry = index[lang] ?? (index[lang] = {} as Record<LegalDocKind, LegalDoc>);
    entry[kind] = {
      title: doc.meta.title ?? '',
      updated: doc.meta.updated ?? '',
      html: doc.html,
    };
  }
  // Un documento mancante non deve scoprirsi in produzione: le pagine sono
  // pre-renderizzate e il conteggio atteso salterebbe, ma questo dice il perche'.
  for (const lang of RULES_LANGS) {
    for (const kind of ['terms', 'privacy'] as const) {
      if (!index[lang]?.[kind]) throw new Error(`documento legale mancante: ${lang}/${kind}.md`);
    }
  }
  return index;
}

const LEGAL_DOCS = buildIndex();

/**
 * Il documento nella lingua data. Un tag sconosciuto ricade sull'inglese: e' la
 * stessa regola di `fallbackLng` dell'i18n, applicata al contenuto.
 */
export function getLegalDoc(lang: string, kind: LegalDocKind): LegalDoc {
  const exact = (RULES_LANGS as readonly string[]).includes(lang) ? (lang as RulesLang) : null;
  if (exact) return LEGAL_DOCS[exact][kind];
  const base = lang.split('-')[0]?.toLowerCase();
  const matched = RULES_LANGS.find((code) => code.split('-')[0] === base);
  return LEGAL_DOCS[matched ?? DEFAULT_LANG][kind];
}
